<script lang="ts">
  import { POINTS_LEVEL } from '$lib/session/points'

  interface Props {
    xp: number
    targetXp?: number | undefined
    multiplier?: number
    showLevel?: boolean
    showProgress?: boolean
  }

  let {
    xp,
    targetXp = undefined,
    multiplier = 1,
    showLevel = false,
    showProgress = false,
  }: Props = $props()

  let level = $derived(Math.trunc(xp / POINTS_LEVEL) + 1)
  // svelte-ignore state_referenced_locally
  let initialXp = xp

  let progress = $derived((xp % POINTS_LEVEL) / POINTS_LEVEL)
  let realTargetProgress = $derived(
    targetXp ? (targetXp % POINTS_LEVEL) / POINTS_LEVEL : 0,
  )
  // If the target progress is less, then we wait until the progress reaches
  // the maximum and then show it again.
  let targetProgress = $derived(
    realTargetProgress < progress ? 1 : realTargetProgress,
  )

  let progressing = $state(false)

  let progressingTimeout: number | undefined = $state()

  $effect(() => {
    if (initialXp !== xp) {
      progressing = true
      initialXp = xp
      window.clearTimeout(progressingTimeout)
      progressingTimeout = window.setTimeout(() => {
        progressing = false
      }, 800)
    }
  })

  let tier = $derived(Math.floor(Math.log10(level)))
  let tierLevel = $derived(level < 10 ? level : Math.floor(level / 10 ** tier))

  let number = $derived(showLevel ? level : tierLevel)

  // svelte-ignore state_referenced_locally
  let initialNumber = number

  let fontSizeMultiplier = $derived(
    1.0 - (`${number}`.length - 1) * 0.1 + (showLevel ? 0 : 0.2),
  )
</script>

<div
  class="level tier-{tier}"
  class:show-tier={!showLevel}
  style:--progress={progress * 100}
  style:--target-progress={targetProgress * 100}
  class:max-multiplier={multiplier >= 10}
  style:--font-size-multiplier={fontSizeMultiplier}
>
  {#if showProgress}
    <div class="progress" class:progressing>
      <svg viewBox="0 0 100 100" stroke-width="1">
        <circle class="outline" fill="none" cx="50" cy="50" r="46"></circle>
        {#if targetXp}
          <circle class="target-progress" fill="none" cx="50" cy="50" r="46"
          ></circle>
        {/if}
        <circle class="progress" fill="none" cx="50" cy="50" r="46"></circle>
      </svg>
    </div>
  {/if}

  <span class="label">
    {#if showLevel}Level{:else}Tier level{/if}
  </span>

  {#key number}
    <div
      class="number"
      data-value={number}
      class:new-value={initialNumber !== number}
    >
      <span>{number}</span>
    </div>
  {/key}
</div>

<style lang="postcss">
  @keyframes explode {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(4);
      opacity: 0;
    }
  }

  @keyframes rainbow-color {
    0% {
      fill: var(--color-bg-accent-secondary);
    }
    30% {
      fill: var(--color-bg-success-secondary);
    }
    60% {
      fill: var(--color-bg-brand-secondary);
    }
    100% {
      fill: var(--color-bg-accent-secondary);
    }
  }

  @keyframes shimmer {
    0%,
    40% {
      background-position-x: 0%;
    }
    50%,
    100% {
      background-position-x: 100%;
    }
  }

  .level {
    position: relative;
    border-radius: var(--radius-full);
    width: 100%;
    height: 100%;
    line-height: 100%;
    text-align: center;

    --_number-scale: calc(var(--font-size-multiplier) * 50cqw);

    &.tier-0 {
      --_tier-bg: #f1e3d6;
      --_tier-color: #8b6443;
    }
    &.tier-1 {
      --_tier-bg: #eeeeee;
      --_tier-color: #767676;
    }
    &.tier-2 {
      --_tier-bg: #fff9b5;
      --_tier-color: #d28f00;
    }
    &.tier-3 {
      --_tier-bg: #cff6ff;
      --_tier-color: #00c3ef;
    }

    &.show-tier {
      &::before,
      &::after {
        position: absolute;
        inset: 0;
        border-radius: var(--radius-full);
        content: '';
      }

      &::after {
        /* Shimmer */
        animation: shimmer 10s infinite linear;
        --_shimmer-color: color-mix(in oklch, var(--_tier-color), white 97%);
        background: linear-gradient(
          -45deg,
          transparent 45%,
          var(--_shimmer-color) 45%,
          var(--_shimmer-color) 55%,
          transparent 55%
        );
        background-position-x: 100%;
        background-size: 300%;
      }
    }

    &.show-tier {
      border: 0.1em solid color-mix(in srgb, currentColor 50%, transparent);
      background: var(--_tier-bg);
      color: var(--_tier-color);
      /* color: white;
      text-shadow: 0.03em 0.03em 0 var(--_tier-color); */
    }

    .label {
      display: none;
      position: absolute;
      top: 15%;
      left: 50%;
      translate: -50% 0;
      font-size: var(--scale-000);
      @container (width > 60px) {
        display: block;
      }
    }
    .number {
      display: grid;
      position: absolute;
      place-content: center;
      inset: 0;
      font-weight: var(--weight-bold);
      font-size: var(--_number-scale);
      line-height: 100%;
      @container (width > 60px) {
        top: 10%;
      }

      span {
        position: relative;
      }
      &.new-value::after {
        display: grid;
        position: absolute;
        place-content: center;
        transform-origin: center;
        animation: explode 0.8s ease-out forwards;
        inset: 0;
        pointer-events: none;
        content: attr(data-value);
        color: var(--color-text-brand);
      }
    }

    &.max-multiplier {
      /* TODO */
    }
  }
  .progress {
    position: absolute;
    inset: 0;
    --circumference: calc(2 * 3.14159265359 * 46);
    --width: 0.125rem;

    @container (width < 4rem) {
      --width: 0.375rem;
    }
    &.progressing {
      .progress {
        stroke-width: calc(var(--width) * 2.5);
      }
      .target-progress {
        stroke-width: calc(var(--width) * 2.5);
      }
    }
    svg {
      position: absolute;
      rotate: -0.25turn;
      stroke-width: var(--width);
      inset: 0;
      circle {
        transition: stroke-width 200ms linear;
        &.outline {
          fill: var(--color-bg-surface);
          stroke: var(--color-border);
        }
        &.progress {
          stroke: var(--color-text);
          @container (width > 60px) {
            stroke: var(--color-text);
          }
          stroke-dasharray: var(--circumference);
          stroke-dashoffset: calc(
            var(--circumference) * ((100 - var(--progress)) / 100)
          );
        }
        &.target-progress {
          stroke: var(--color-text-success);
          stroke-dasharray: var(--circumference);
          stroke-dashoffset: calc(
            var(--circumference) * ((100 - var(--target-progress)) / 100)
          );
        }
      }
    }
  }
</style>
